import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Key, Trash2, Eye, EyeOff, Crown, Sparkles } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { redirectToCheckout } from "@/lib/stripe";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import OnboardingModal from "@/components/OnboardingModal";
import NotificationSettings from "@/components/NotificationSettings";

// Schema for password validation
const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Confirm password is required")
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  const { user, signOut, isPremium: contextIsPremium } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const isMobile = useIsMobile();
  
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });
  
  useEffect(() => {
    // Set page title for SEO
    document.title = "Account Settings | Habitualy";
    setIsLoading(false);
  }, []);

  const handleUpgrade = async () => {
    if (!user) return;
    
    try {
      setIsUpgrading(true);
      if (!user.email) {
        toast.error("No email associated with account");
        return;
      }
      await redirectToCheckout(user.id, user.email);
    } catch (error) {
      console.error("Error starting checkout:", error);
      toast.error("Failed to start checkout process. Please try again.");
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleChangePassword = async (values: PasswordFormValues) => {
    try {
      setIsChangingPassword(true);
      
      // First verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || "",
        password: values.currentPassword
      });
      
      if (signInError) {
        toast.error("Current password is incorrect");
        setIsChangingPassword(false);
        return;
      }
      
      // If current password is correct, proceed to update the password
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });
      
      if (error) throw error;
      
      toast.success("Password updated successfully");
      form.reset();
    } catch (error: any) {
      toast.error(error.message || "Error updating password");
    } finally {
      setIsChangingPassword(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    try {
      // In a real app, you would need to create a server-side function to delete the user
      // since client-side deletion is not supported directly in Supabase
      
      // For now, we'll just sign the user out as a placeholder
      await signOut();
      toast.success("Your account has been deleted");
    } catch (error: any) {
      toast.error("Error deleting account");
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="max-w-md mx-auto">
          <Card className="shadow-md">
            <CardHeader className="border-b bg-muted/50">
              <CardTitle>Account Settings</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Email</p>
                    <p className="bg-muted/50 px-3 py-2 rounded border text-sm">{user?.email}</p>
                  </div>
                  
                  {/* Premium Status Section */}
                  <div className="border-t pt-5">
                    <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Crown className="h-4 w-4 text-yellow-500" />
                      Premium Status
                    </h3>
                    {contextIsPremium ? (
                      <div className="bg-muted/50 px-3 py-2 rounded border">
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                          Premium Member ✨
                        </p>
                      </div>
                    ) : (
                      <Button
                        onClick={handleUpgrade}
                        disabled={isUpgrading}
                        className="w-full"
                      >
                        {isUpgrading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Starting checkout...
                          </>
                        ) : (
                          <>
                            <Crown className="mr-2 h-4 w-4" />
                            Upgrade to Premium
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Onboarding Guide */}
                  <div className="border-t pt-5">
                    <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Onboarding Guide
                    </h3>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowOnboarding(true)}
                    >
                      View onboarding
                    </Button>
                  </div>

                  {/* Notification Settings */}
                  <div className="border-t pt-5">
                    <NotificationSettings />
                  </div>
                  
                  {/* Password Change Section */}
                  <div className="border-t pt-5">
                    <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Change Password
                    </h3>
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(handleChangePassword)} className="space-y-4">
                        <FormField
                          control={form.control}
                          name="currentPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Current Password</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showCurrentPassword ? "text" : "password"}
                                    {...field}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                  >
                                    {showCurrentPassword ? (
                                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="newPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>New Password</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showNewPassword ? "text" : "password"}
                                    {...field}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                  >
                                    {showNewPassword ? (
                                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Confirm New Password</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showConfirmPassword ? "text" : "password"}
                                    {...field}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                  >
                                    {showConfirmPassword ? (
                                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <Button 
                          type="submit" 
                          disabled={isChangingPassword}
                          className="w-full"
                        >
                          {isChangingPassword ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Updating...
                            </>
                          ) : "Update Password"}
                        </Button>
                      </form>
                    </Form>
                  </div>
                  
                  <div className="border-t pt-5">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="w-full flex items-center justify-center gap-2">
                          <Trash2 className="h-4 w-4" />
                          Delete Account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your
                            account and remove your data from our servers.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDeleteAccount}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete Account
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
      <OnboardingModal
        open={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
        isPremium={contextIsPremium}
      />
    </div>
  );
}
